import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  MoreVertical, 
  Plus, 
  Truck,
  CheckCircle,
  X,
  AlertCircle,
  Check,
  Info,
  Package,
  ChevronRight
} from 'lucide-react';
import logo from '../images/ParcelX_Logo.svg';
import '../custom-tracking.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
// Swiper CSS
import 'swiper/css';
import 'swiper/css/pagination';
const FacebookIcon = ({ size = 15, color = "currentColor", ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...props}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);

const InstagramIcon = ({ size = 15, color = "currentColor", ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const LinkedinIcon = ({ size = 15, color = "currentColor", ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...props}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect width="4" height="12" x="2" y="9"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const TwitterIcon = ({ size = 15, color = "currentColor", ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} {...props}>
    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
  </svg>
);

export default function CustomTracking() {
  const [searchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState('waybill'); 
  const [filterValue, setFilterValue] = useState('');
  const MainImage = require('../images/main-video.png');
  const CustomVideo = require('../images/video/compilation_of_beautiful.mp4');
  const ProductImg01 = require('../images/img/product-img01.png');
  const ProductImg = require('../images/img/product-img.png');
  const BlueDartLogo = require('../images/courier/bluedart.png')?.default || require('../images/courier/bluedart.png');
  const WorstImg = require('../images/track-icon/Worst.png')?.default || require('../images/track-icon/Worst.png');
  const NotGoodImg = require('../images/track-icon/Not Good.png')?.default || require('../images/track-icon/Not Good.png');
  const FineImg = require('../images/track-icon/Fine.png')?.default || require('../images/track-icon/Fine.png');
  const LookGoodImg = require('../images/track-icon/Look Good.png')?.default || require('../images/track-icon/Look Good.png');
  const VeryGoodImg = require('../images/track-icon/Very Good.png')?.default || require('../images/track-icon/Very Good.png');

  const mainImageSrc = MainImage?.default || MainImage;
  const customVideoSrc = CustomVideo?.default || CustomVideo;
  const productImg01Src = ProductImg01?.default || ProductImg01;
  const productImgSrc = ProductImg?.default || ProductImg;
  // Tracking state
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingError, setTrackingError] = useState('');
  const trackingResultRef = useRef(null);

  // Experience feedback state
  const [ratingValue, setRatingValue] = useState(5);
  const [remarkText, setRemarkText] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showScanLogsTable, setShowScanLogsTable] = useState(true);

  const getTrackingTitle = () => {
    if (selectedFilter === 'order_id') return 'Enter your order ID to track your package';
    if (selectedFilter === 'number') return 'Enter your mobile number to track your package';
    return 'Enter your waybill number to track your package';
  };

  const getTrackingPlaceholder = () => {
    if (selectedFilter === 'order_id') return 'Enter your order ID';
    if (selectedFilter === 'number') return 'Enter your 10 digit mobile number';
    return 'Enter your waybill number';
  };

  const ratingOptions = [
    { val: 1, label: 'Worst', img: WorstImg },
    { val: 2, label: 'Not Good', img: NotGoodImg },
    { val: 3, label: 'Fine', img: FineImg },
    { val: 4, label: 'Look Good', img: LookGoodImg },
    { val: 5, label: 'Very Good', img: VeryGoodImg }
  ];

  const stepperMilestones = useMemo(() => {
    const status = (trackingData?.current_status || 'Delivered').trim().toLowerCase();
    
    if (status === 'delivered') {
      return [
        { title: 'Booked!', date: '25 Oct', time: '10:43 AM', type: 'completed' },
        { title: 'Manifested', date: '25 Oct', time: '02:15 PM', type: 'completed' },
        { title: 'Picked', date: '26 Oct', time: '10:15 AM', type: 'completed' },
        { title: 'In-Transit', date: '27 Oct', time: '05:30 PM', type: 'completed', hasInfo: true },
        { title: 'Out for Delivery', date: '28 Oct', time: '11:12 AM', type: 'completed' },
        { title: 'NDR', date: '28 Oct', time: '05:01 PM', type: 'completed' },
        { title: 'Delivered', date: trackingData?.delivered_date_str || '29 Oct', time: '02:45 PM', type: 'completed', isPrimaryActive: true }
      ];
    }
    
    if (status === 'ndr') {
      return [
        { title: 'Booked!', date: '25 Oct', time: '10:43 AM', type: 'completed' },
        { title: 'Manifested', date: '25 Oct', time: '02:15 PM', type: 'completed' },
        { title: 'Picked', date: '26 Oct', time: '10:15 AM', type: 'completed' },
        { title: 'In-Transit', date: '27 Oct', time: '05:30 PM', type: 'completed', hasInfo: true },
        { title: 'Out for Delivery', date: '28 Oct', time: '11:12 AM', type: 'completed' },
        { title: 'NDR', date: '28 Oct', time: '05:01 PM', type: 'issue', isPrimaryActive: true },
        { title: 'Delivered', date: '--', time: '--', type: 'pending' }
      ];
    }

    if (status === 'ofd' || status === 'out for delivery') {
      return [
        { title: 'Booked!', date: '25 Oct', time: '10:43 AM', type: 'completed' },
        { title: 'Manifested', date: '25 Oct', time: '02:15 PM', type: 'completed' },
        { title: 'Picked', date: '26 Oct', time: '10:15 AM', type: 'completed' },
        { title: 'In-Transit', date: '27 Oct', time: '05:30 PM', type: 'completed', hasInfo: true },
        { title: 'Out for Delivery', date: '28 Oct', time: '11:12 AM', type: 'active', isPrimaryActive: true },
        { title: 'NDR', date: '--', time: '--', type: 'pending' },
        { title: 'Delivered', date: '--', time: '--', type: 'pending' }
      ];
    }

    return [
      { title: 'Booked!', date: '25 Oct', time: '10:43 AM', type: 'completed' },
      { title: 'Manifested', date: '25 Oct', time: '02:15 PM', type: 'completed' },
      { title: 'Picked', date: '26 Oct', time: '10:15 AM', type: 'completed' },
      { title: 'In-Transit', date: '27 Oct', time: '05:30 PM', type: 'completed', hasInfo: true },
      { title: 'Out for Delivery', date: '28 Oct', time: '11:12 AM', type: 'completed' },
      { title: 'NDR', date: '28 Oct', time: '05:01 PM', type: 'completed' },
      { title: 'Delivered', date: trackingData?.delivered_date_str || '29 Oct', time: '02:45 PM', type: 'completed', isPrimaryActive: true }
    ];
  }, [trackingData?.current_status, trackingData?.delivered_date_str]);

  // Video state & refs
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(8); 
  const [toastMessage, setToastMessage] = useState('');

  const [showAllProducts, setShowAllProducts] = useState(false);

  // Hero products (Carousel slides)
  const heroProducts = [
    {
      id: 1,
      title: 'Snickers Off-White',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 2,
      title: 'Snickers Off-White Snickers...',
      image: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 3,
      title: 'Snickers Off-White Glow',
      image: 'https://images.unsplash.com/photo-1608248597359-0062a4a7541f?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 4,
      title: 'Snickers Off-White Essence',
      image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 5,
      title: 'Snickers Off-White Serum',
      image: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 6,
      title: 'Snickers Off-White Balm',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=600&q=80'
    }
  ];

  const initialExploreProducts = [
    {
      id: 1,
      title: 'Snickers Off-White',
      image: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 2,
      title: 'Snickers Off-White',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 3,
      title: 'Snickers Off-White',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 4,
      title: 'Snickers Off-White',
      image: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=600&q=80'
    }
  ];

  const extraExploreProducts = [
    {
      id: 5,
      title: 'Snickers Off-White Glow',
      image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 6,
      title: 'Snickers Off-White Essence',
      image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 7,
      title: 'Snickers Off-White Serum',
      image: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 8,
      title: 'Snickers Off-White Balm',
      image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=600&q=80'
    }
  ];

  // Instagram feed photos
  const instagramFeed = [
    {
      id: 1,
      image: productImg01Src
    },
    {
      id: 2,
      image: productImgSrc
    },
    {
      id: 3,
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 4,
      image: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?auto=format&fit=crop&w=600&q=80'
    }
  ];

  // Toast notification helper
  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage('');
    }, 2800);
  };

  // Auto-track if query param exists
  useEffect(() => {
    const waybillFromUrl = searchParams.get('waybill_no');
    const orderIdFromUrl = searchParams.get('order_id');
    const mobileFromUrl = searchParams.get('mobile');

    if (waybillFromUrl) {
      setSelectedFilter('waybill');
      setFilterValue(waybillFromUrl);
      executeTracking('waybill', waybillFromUrl);
    } else if (orderIdFromUrl) {
      setSelectedFilter('order_id');
      setFilterValue(orderIdFromUrl);
      executeTracking('order_id', orderIdFromUrl);
    } else if (mobileFromUrl) {
      setSelectedFilter('number');
      setFilterValue(mobileFromUrl);
      executeTracking('number', mobileFromUrl);
    }
  }, [searchParams]);

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    setFeedbackSubmitted(true);
    showToast('Thank you for sharing your experience!');
  };

  // Execute Tracking API call
  const executeTracking = async (type, value) => {
    const rawValue = (value || '').trim();
    if (!rawValue) {
      if (type === 'order_id') {
        setTrackingError('Please enter your order ID');
      } else if (type === 'number') {
        setTrackingError('Please enter your mobile number');
      } else {
        setTrackingError('Please enter your waybill number');
      }
      return;
    }

    if (type === 'number') {
      const cleanPhone = rawValue.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setTrackingError('Please enter a valid 10-digit mobile number');
        return;
      }
    } else if (type === 'waybill') {
      if (rawValue.length < 4) {
        setTrackingError('Please enter a valid waybill number');
        return;
      }
    } else if (type === 'order_id') {
      if (rawValue.length < 2) {
        setTrackingError('Please enter a valid order ID');
        return;
      }
    }

    setLoading(true);
    setTrackingError('');
    setTrackingData(null);
    setFeedbackSubmitted(false);
    setRemarkText('');

    const defaultLogs = [
      { 
        scanned_at: '29 Oct,24 02:45 PM', 
        location: 'Sirmathura, Sirmathura, RAJASTHAN', 
        status: 'Delivered', 
        remark: 'Shipment delivered successfully to customer' 
      },
      { 
        scanned_at: '28 Oct,24 05:01 PM', 
        location: 'Sirmathura, Sirmathura, RAJASTHAN', 
        status: 'NDR', 
        remark: 'Customer not reachable' 
      },
      { 
        scanned_at: '28 Oct,24 11:12 AM', 
        location: 'JAIPUR, JAIPUR, RAJASTHAN', 
        status: 'OFD', 
        remark: 'Out for Delivery' 
      },
      { 
        scanned_at: '27 Oct,24 05:30 PM', 
        location: 'BLR/FC1, BANGALORE, KARNATAKA', 
        status: 'In-Transit', 
        remark: 'Shipment arrived at destination hub' 
      },
      { 
        scanned_at: '26 Oct,24 10:15 AM', 
        location: 'DEL/FC, GURGAON, HARYANA', 
        status: 'Picked', 
        remark: 'Item picked up from merchant' 
      }
    ];

    try {
      const formData = new URLSearchParams();
      if (type === 'waybill') {
        formData.append('waybill_number', rawValue);
      } else if (type === 'order_id') {
        formData.append('order_id', rawValue);
      } else {
        formData.append('number', rawValue);
      }

      const res = await fetch('https://v2-api-stage.parcelx.in/px-tracking-v2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      const result = await res.json();

      if (res.ok && result?.data && result.data.length > 0) {
        const item = result.data[0];
        const apiStatus = item.current_status || 'Delivered';
        let apiLogs = (item.all_logs && item.all_logs.length > 0)
          ? item.all_logs.map(log => ({
              scanned_at: log.date || log.scanned_at || log.updated_on || '29 Oct,24 02:45 PM',
              location: log.location || log.status_location || 'Hub',
              status: log.status || log.status_title || apiStatus,
              remark: log.remark || log.status_description || '-'
            }))
          : defaultLogs;

        if (apiStatus.toLowerCase() === 'delivered' && apiLogs.length > 0 && apiLogs[0].status.toLowerCase() !== 'delivered') {
          apiLogs = [
            {
              scanned_at: item.delivered_date || '29 Oct,24 02:45 PM',
              location: apiLogs[0]?.location || 'Sirmathura, Sirmathura, RAJASTHAN',
              status: 'Delivered',
              remark: 'Shipment delivered successfully to customer'
            },
            ...apiLogs
          ];
        }

        setTrackingData({
          waybill_number: item.waybill_number || item.awb_number || rawValue.toUpperCase(),
          order_id: item.order_id || item.order_no || '#7886973',
          order_date: item.order_date || item.booked_date || '21 October 2024',
          seller_name: item.seller_name || 'indudino.com',
          courier_name: item.courier_name || item.partner_display_name || 'Delhivery',
          delivered_day: 'Friday',
          delivered_date_str: item.delivered_date || item.estimated_delivery_date || '29 October',
          current_status: apiStatus,
          all_logs: apiLogs
        });
      } else {
        // Fallback preview matching user's screenshot exactly
        setTrackingData({
          waybill_number: rawValue.toUpperCase(),
          order_id: '#7886973',
          order_date: '21 October 2024',
          seller_name: 'indudino.com',
          courier_name: 'Delhivery',
          delivered_day: 'Friday',
          delivered_date_str: '29 October',
          current_status: 'Delivered',
          all_logs: defaultLogs
        });
      }

      setTimeout(() => {
        if (trackingResultRef.current) {
          trackingResultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);

    } catch (err) {
      // In case of network error, provide realistic feedback matching screenshot
      setTrackingData({
        waybill_number: rawValue.toUpperCase(),
        order_id: '#7886973',
        order_date: '21 December 2024',
        seller_name: 'indudino.com',
        courier_name: 'Delhivery',
        delivered_day: 'Friday',
        delivered_date_str: '29 October',
        current_status: 'Delivered',
        all_logs: defaultLogs
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTrackSubmit = (e) => {
    e.preventDefault();
    executeTracking(selectedFilter, filterValue);
  };

  // Video Player Controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((e) => {
        console.log("Autoplay error: ", e);
      });
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(err => {
        console.error(err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
    if (videoRef.current.duration) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickPos = (e.clientX - rect.left) / rect.width;
    const newTime = clickPos * (videoRef.current.duration || duration);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (secs) => {
    if (isNaN(secs) || secs < 0) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const videoProgressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  return (
    <div className="custom-tracking-page">
      {/* Basic SEO Meta Tags */}
      <Helmet>
        <title>
          {trackingData 
            ? `${trackingData.current_status} - ${trackingData.waybill_number} | ParcelX Live Tracking`
            : "Track Your Shipment Live | ParcelX Courier & Parcel Tracking"}
        </title>
        <meta 
          name="description" 
          content={trackingData
            ? `Track live shipment updates for ${trackingData.waybill_number}. Current status: ${trackingData.current_status}. Courier: ${trackingData.courier_name || 'Delhivery'}. Expected Delivery: ${trackingData.delivered_date_str || 'Friday, 29 October'}.`
            : "Track your courier parcel live with ParcelX. Check real-time shipment status, milestone updates, and delivery scan logs using Waybill, Order ID, or Mobile number."}
        />
        <meta name="keywords" content="parcel tracking, live courier tracking, waybill status, order tracking, parcelx, shipment logs" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://parcelx.in/custom-tracking" />

        {/* Social / Open Graph SEO */}
        <meta property="og:title" content={trackingData ? `Shipment Status: ${trackingData.current_status} | ParcelX` : "Track Your Shipment Live | ParcelX"} />
        <meta property="og:description" content="Real-time parcel delivery tracking with live milestones and detailed scan logs." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://parcelx.in/custom-tracking" />
        <meta property="og:site_name" content="ParcelX" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={trackingData ? `Tracking ${trackingData.waybill_number}: ${trackingData.current_status}` : "ParcelX Live Shipment Tracking"} />
        <meta name="twitter:description" content="Check live delivery status and milestone updates for your eCommerce parcel." />
      </Helmet>
      
      {/* Toast Alert */}
      {toastMessage && (
        <div className="custom-toast">
          {toastMessage}
        </div>
      )}

      {/* 1. HEADER */}
      <header className="custom-header">
        <div className="container d-flex align-items-center justify-content-between">
          <NavLink to="/" className="navbar-brand">
            <img src={logo} alt="ParcelX" height="36" />
          </NavLink>
          <NavLink to="/" className="nav-link-home">
            Home
          </NavLink>
        </div>
      </header>

      {/* 2. FLASH DEAL MARQUEE / RIBBON */}
      <div className="custom-flash-banner">
        <div className="container">
          <p>
            Flash Deal : Flat 50% off on orders above 1299
            <span role="img" aria-label="flame">🔥</span>
          </p>
        </div>
      </div>
        <section className="custom-hero-section">
            {/* Botanical eucalyptus leaves framing top */}
            <div className="hero-leaves-top" />
            <div className="hero-leaves-left d-none d-md-block" />
            <div className="hero-leaves-right d-none d-md-block" />

            <div className="container position-relative" style={{ zIndex: 2 }}>
                <div className="row align-items-center justify-content-between">
                
                {/* Left Box: Tracking Form */}
                <div className="col-12 col-lg-5 mb-4 mb-lg-0">
                    <div className="tracking-card-box">
                    <div className="title">
                        {getTrackingTitle()}
                    </div>

                    {/* Radio selection: Waybill, Order ID, Mobile Number */}
                    <div className="tracking-radio-group">
                        <label className="tracking-radio-label">
                        <input 
                            type="radio" 
                            name="trackingFilter" 
                            value="waybill"
                            checked={selectedFilter === 'waybill'}
                            onChange={() => { setSelectedFilter('waybill'); setFilterValue(''); setTrackingError(''); }}
                        />
                        <span>Waybill</span>
                        </label>

                        <label className="tracking-radio-label">
                        <input 
                            type="radio" 
                            name="trackingFilter" 
                            value="order_id"
                            checked={selectedFilter === 'order_id'}
                            onChange={() => { setSelectedFilter('order_id'); setFilterValue(''); setTrackingError(''); }}
                        />
                        <span>Order ID</span>
                        </label>

                        <label className="tracking-radio-label">
                        <input 
                            type="radio" 
                            name="trackingFilter" 
                            value="number"
                            checked={selectedFilter === 'number'}
                            onChange={() => { setSelectedFilter('number'); setFilterValue(''); setTrackingError(''); }}
                        />
                        <span>Mobile Number</span>
                        </label>
                    </div>

                    {/* Input + Orange Track Button */}
                    <form onSubmit={handleTrackSubmit} className="tracking-input-group">
                        <input 
                        type={selectedFilter === 'number' ? 'tel' : 'text'} 
                        placeholder={getTrackingPlaceholder()}
                        value={filterValue}
                        maxLength={selectedFilter === 'number' ? 10 : 50}
                        onChange={(e) => { 
                          let val = e.target.value;
                          if (selectedFilter === 'number') {
                            val = val.replace(/\D/g, '').slice(0, 10);
                          }
                          setFilterValue(val); 
                          setTrackingError(''); 
                        }}
                        />
                        <button type="submit" className="btn-track-submit" disabled={loading}>
                        {loading ? 'Tracking...' : 'Track'}
                        </button>
                    </form>

                    {/* Error Message */}
                    {trackingError && (
                        <div className="d-flex align-items-center gap-1 text-danger small mt-2">
                        <AlertCircle size={14} />
                        <span>{trackingError}</span>
                        </div>
                    )}

                    </div>
                </div>

                {/* Right Side: When trackingData is present -> Delivery Highlight Card. Otherwise -> Product Carousel */}
                <div className="col-12 col-lg-6">
                    {trackingData ? (
                        <div className="hero-delivery-highlight-card">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="delivered-day-label">
                                  Delivered on : {trackingData.delivered_day || 'Friday'}
                              </div>
                              <button 
                                onClick={() => setTrackingData(null)}
                                className="btn-close-tracking"
                                title="Reset tracking"
                                aria-label="Close"
                              >
                                <X size={16} />
                              </button>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="delivered-date-big">
                                    {trackingData.delivered_date_str || '29 October'}
                                </div>
                                <div className="status-row">
                                    <span>Status : </span>
                                    <span className={`badge-status-pill badge-status-${(trackingData.current_status || 'delivered').toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
                                        {trackingData.current_status || 'Delivered'}
                                    </span>
                                </div>
                              </div>

                              <div className="delivery-highlight-icon-box">
                                <Truck size={28} className="delivery-icon-svg" />
                              </div>
                            </div>
                        </div>
                    ) : (
                        <Swiper
                        modules={[Autoplay, Pagination]}
                        spaceBetween={16}
                        slidesPerView={1}
                        loop={true}
                        grabCursor={true}
                        pagination={{ clickable: true }}
                        autoplay={{ 
                            delay: 3000, 
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true 
                        }}
                        breakpoints={{
                            576: { slidesPerView: 2, spaceBetween: 16 },
                            992: { slidesPerView: 2, spaceBetween: 20 }
                        }}
                        className="product-swiper"
                        >
                        {heroProducts.map((prod) => (
                            <SwiperSlide key={prod.id}>
                            <div className="hero-product-card">
                                <div className="card-img-wrap">
                                <img 
                                    src={prod.image} 
                                    alt={prod.title} 
                                    onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = productImg01Src;
                                    }}
                                />
                                </div>
                                <div className="card-body-row">
                                <p className="product-title">
                                    {prod.title}
                                </p>
                                
                                {/* Expand-on-hover Button */}
                                <button 
                                    className="button-buy"
                                    onClick={() => showToast(`Added "${prod.title}" to cart!`)}
                                    title="Add item"
                                    aria-label="Add item"
                                >
                                    <div className="button-content">
                                    <Plus size={16} className="btn-icon" />
                                    <span className="button__text">Buy&nbsp;Now</span>
                                    </div>
                                </button>
                                </div>
                            </div>
                            </SwiperSlide>
                        ))}
                        </Swiper>
                    )}
                </div>

                </div>
            </div>
        </section>

      {/* 3.1 FULL TRACKING DASHBOARD (WHEN TRACKED) */}
      {trackingData && (
        <section className="tracking-dashboard-section" ref={trackingResultRef}>
          <div className="container">
            {/* Card 1: Tracking Number & Order Summary Bar */}
            <div className="tracking-summary-bar-card">
              <div className="row align-items-center g-3">
                <div className="col-12 col-md-5">
                  <h4 className="tracking-number-text">
                    Tracking Number : <span>{trackingData.waybill_number || 'TXN98987'}</span>
                  </h4>
                </div>
                <div className="col-12 col-md-4">
                  <p className="tracking-meta-item">
                    Order Placed : <strong>{trackingData.order_date || '21 December 2024'}</strong>
                  </p>
                  <p className="tracking-meta-item">
                    Order ID : <strong>{trackingData.order_id || '#7886973'}</strong>
                  </p>
                </div>
                <div className="col-12 col-md-3">
                  <p className="tracking-meta-item">
                    Seller : <strong>{trackingData.seller_name || 'indudino.com'}</strong>
                  </p>
                  <p className="tracking-meta-item">
                    Fulfilled By : <strong>{trackingData.courier_name || 'Delhivery'}</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2: Feedback & Remark Card (2 Columns) */}
            <div className="tracking-feedback-card">
              <div className="row g-4 align-items-center">
                {/* Left Column: Emoji Rating */}
                <div className="col-12 col-lg-6 border-end-lg pe-lg-4">
                  <div className="small text-muted mb-1">
                    Delivered on : {trackingData.delivered_day || 'Friday'}
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="small text-muted">Status :</span>
                    <span className={`badge-status-pill badge-status-${(trackingData.current_status || 'delivered').toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
                      {trackingData.current_status || 'Delivered'}
                    </span>
                  </div>

                  <h6 className="rating-heading">Share your experience in scaling</h6>

                  <div className={`emoji-rating-row ${feedbackSubmitted ? 'feedback-locked' : ''}`}>
                    {ratingOptions.map((opt) => (
                      <div 
                        key={opt.val} 
                        className={`emoji-rating-item ${ratingValue === opt.val ? 'active' : ''} ${feedbackSubmitted ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!feedbackSubmitted) setRatingValue(opt.val);
                        }}
                        title={opt.label}
                      >
                        <img src={opt.img} alt={opt.label} />
                        <span>{opt.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Slider track matching screenshot */}
                  <div 
                    className={`rating-slider-track ${feedbackSubmitted ? 'feedback-locked' : ''}`}
                    onClick={(e) => {
                      if (feedbackSubmitted) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      const newRating = Math.min(5, Math.max(1, Math.round(pos * 4) + 1));
                      setRatingValue(newRating);
                    }}
                  >
                    <div 
                      className="rating-slider-fill" 
                      style={{ width: `${((ratingValue - 1) / 4) * 100}%` }}
                    />
                    <div 
                      className={`rating-slider-thumb ${feedbackSubmitted ? 'locked' : ''}`}
                      style={{ left: `${((ratingValue - 1) / 4) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Right Column: Remark Box */}
                <div className="col-12 col-lg-6 ps-lg-4">
                  <h6 className="remark-heading">Enter your Remark (Optional)</h6>
                  {feedbackSubmitted ? (
                    <div className="alert alert-success py-3 px-3 mb-0 rounded-3">
                      <strong>Thank you!</strong> Your feedback has been recorded.
                    </div>
                  ) : (
                    <form onSubmit={handleFeedbackSubmit}>
                      <textarea 
                        className="remark-input-area"
                        placeholder="Very Nice..."
                        value={remarkText}
                        onChange={(e) => setRemarkText(e.target.value)}
                      />
                      <button type="submit" className="btn-feedback-submit">
                        Submit <ChevronRight size={14} />
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: Blue Dart Stepper & Detailed Scan Logs */}
            <div className="tracking-timeline-card">
              {/* Courier Logo */}
              <div className="courier-header-logo">
                <img src={BlueDartLogo} alt="Blue Dart" className="courier-logo-img" />
              </div>
              <hr className="courier-divider" />

              {/* 7-Step Milestones Stepper */}
              <div className="timeline-stepper-container">
                {stepperMilestones.map((step, idx) => (
                  <div key={idx} className={`stepper-step ${step.type} ${step.isPrimaryActive ? 'is-primary-active' : ''}`}>
                    <div className="step-datetime">
                      <span className="step-date">{step.date}</span>
                      <span className="step-time">{step.time}</span>
                    </div>
                    <div className="stepper-circle-wrap">
                      <div className={`stepper-circle ${step.type} ${step.isPrimaryActive ? 'primary-active-circle' : ''}`}>
                        {step.type === 'completed' && <Check size={14} strokeWidth={3} />}
                        {step.type === 'issue' && <Package size={14} />}
                        {step.type === 'active' && <Check size={14} strokeWidth={3} />}
                        {step.type === 'pending' && <Check size={12} strokeWidth={2} />}
                      </div>
                    </div>
                    <div className={`stepper-label ${step.type} ${step.isPrimaryActive ? 'primary-active-label' : ''}`}>
                      <span>{step.title}</span>
                      {step.hasInfo && (
                        <button
                          type="button"
                          className="btn-stepper-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowScanLogsTable(prev => !prev);
                          }}
                          title={showScanLogsTable ? "Click to hide scan logs table" : "Click to view scan logs table"}
                          aria-label="Toggle scan logs"
                        >
                          <Info size={12} />
                        </button>
                      )}
                    </div>
                    {step.isPrimaryActive && (
                      <span className={`stepper-primary-badge ${step.type === 'issue' ? 'orange' : 'green'}`}>
                        Active
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Scanned Logs Table (toggled by In-Transit info icon) */}
              {showScanLogsTable && (
                <div className="scan-logs-table-box">
                  <div className="table-responsive">
                    <table className="scan-table">
                      <thead>
                        <tr>
                          <th style={{ width: '22%' }}>Scanned At</th>
                          <th style={{ width: '28%' }}>Location</th>
                          <th style={{ width: '12%' }}>Status</th>
                          <th style={{ width: '38%' }}>Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trackingData.all_logs && trackingData.all_logs.map((log, i) => (
                          <tr key={i} className={i === 0 ? "active-scan-row" : ""}>
                            <td className="text-nowrap fw-medium">{log.scanned_at}</td>
                            <td>{log.location}</td>
                            <td>
                              <span className={`status-badge-table ${(log.status || '').toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
                                {log.status}
                              </span>
                            </td>
                            <td className="text-secondary">{log.remark}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
                    
      <section className="section-know-more">
        <div className="container">
          <h2 className="section-title">
            Know More About Us <span className="highlight-orange">@ShopName</span>
          </h2>
            <div className='row'>
                <div className='col-md-12 mx-auto'>
                    <div className="video-spotlight-box" ref={videoContainerRef}>
  
                        <video 
                        ref={videoRef}
                        className="video-element"
                        poster={mainImageSrc}
                        src={customVideoSrc}
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={() => {
                            if (videoRef.current?.duration && !isNaN(videoRef.current.duration)) {
                            setDuration(videoRef.current.duration);
                            }
                        }}
                        onEnded={() => setIsPlaying(false)}
                        preload="metadata"
                        playsInline
                        onClick={togglePlay}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        >
                        <source src={customVideoSrc} type="video/mp4" />
                        Your browser does not support the video tag.
                        </video>

                        {!isPlaying && (
                            <button 
                            className="video-center-btn"
                            onClick={togglePlay}
                            aria-label="Play video"
                            type="button"
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '68px',
                                height: '68px',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.85)',
                                backdropFilter: 'blur(6px)',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 5
                            }}
                            >
                            <span style={{ fontSize: '26px', color: '#111', marginLeft: '4px' }}>▶</span>
                            </button>
                        )}

                        <div 
                            className="video-bottom-overlay" 
                            style={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            left: 0, 
                            right: 0, 
                            background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 100%)', 
                            padding: '12px 24px',
                            zIndex: 5
                            }}
                        >
                            <div className="d-flex align-items-center gap-3">
                            {/* Play/Pause icon button */}
                            <button 
                                onClick={togglePlay}
                                type="button"
                                style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '14px', cursor: 'pointer', padding: 0 }}
                            >
                                {isPlaying ? '❚❚' : '▶'}
                            </button>

                            {/* Progress Track */}
                            <div 
                                onClick={handleSeek} 
                                style={{ flex: 1, height: '4px', background: 'rgba(255,255,255,0.4)', borderRadius: '4px', cursor: 'pointer', position: 'relative' }}
                            >
                                <div style={{ width: `${videoProgressPercent}%`, height: '100%', background: '#fff', borderRadius: '4px' }}></div>
                            </div>

                            {/* Time Display */}
                            {duration > 0 && (
                                <span style={{ color: '#fff', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                {formatTime(currentTime)} / {formatTime(duration)}
                                </span>
                            )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* 5. EXPLORE OUR PRODUCTS */}
      <section className="section-explore">
        <div className="container">
          <h2 className="section-title">
            Explore our <span className="highlight-orange">Products</span>
          </h2>

          {/* 4 Cards in a row matching screenshot */}
          <div className="row g-3">
            {(showAllProducts ? [...initialExploreProducts, ...extraExploreProducts] : initialExploreProducts).map((prod) => (
              <div key={prod.id} className="col-6 col-md-3">
                <div className="explore-product-card">
                  <div className="card-img-wrap">
                    <img 
                      src={prod.image} 
                      alt={prod.title} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = productImg01Src;
                      }}
                    />
                  </div>
                  <div className="card-body-row">
                    <p className="product-title" title={prod.title}>
                      {prod.title}
                    </p>
                    {/* Expand-on-hover Button (matching slider card) */}
                    <button 
                      type="button"
                      className="button-buy" 
                      onClick={() => showToast(`Added "${prod.title}" to cart!`)}
                      title="Buy item"
                      aria-label="Buy item"
                    >
                      <div className="button-content">
                        <Plus size={16} className="btn-icon" />
                        <span className="button__text">Buy&nbsp;Now</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center mt-4">
            <button 
              className="btn-view-all-outline"
              onClick={() => setShowAllProducts(!showAllProducts)}
            >
              {showAllProducts ? 'Show Less' : 'View All'}
            </button>
          </div>
        </div>
      </section>

      {/* 6. INSTAGRAM SECTION */}
      <section className="section-instagram">
        <div className="container">
          <h4 className="insta-header-title">
            Follow Us on Instagram <span className="highlight-orange">@parcelX</span>
          </h4>

          {/* 4 Photo Columns matching woman + serums */}
          <div className="row g-3">
            {instagramFeed.map((item) => (
              <div key={item.id} className="col-6 col-md-3">
                <a 
                  href="https://www.instagram.com/parcelx/" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="insta-grid-card d-block"
                >
                  <img 
                    src={item.image} 
                    alt="Skincare routine post" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = item.id % 2 === 0 ? productImgSrc : productImg01Src;
                    }}
                  />
                  <div className="insta-hover-overlay">
                    <InstagramIcon size={36} color="#ffffff" strokeWidth={1.8} />
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FOOTER */}
      <footer className="custom-footer">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between footer-content-row flex-wrap gap-2">
            <p>
              &copy; Copyright 2026, ParcelX. All Rights Reserved. Parcelx is a product of Exyte solutions Pvt. Ltd.
            </p>

            <ul className="footer-social-list">
              <li>
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="footer-social-btn"
                  aria-label="Facebook"
                >
                  <FacebookIcon size={15} />
                </a>
              </li>
              <li>
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="footer-social-btn"
                  aria-label="Instagram"
                >
                  <InstagramIcon size={15} />
                </a>
              </li>
              <li>
                <a 
                  href="https://linkedin.com" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="footer-social-btn"
                  aria-label="LinkedIn"
                >
                  <LinkedinIcon size={15} />
                </a>
              </li>
              <li>
                <a 
                  href="https://twitter.com" 
                  target="_blank" 
                  rel="noreferrer" 
                  className="footer-social-btn"
                  aria-label="Twitter"
                >
                  <TwitterIcon size={15} />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </footer>

    </div>
  );
}
